import React, { Key, useEffect, useMemo, useState } from "react";
import "./PolicyTreeTable.less";
import { Col, Row, Table, Tooltip, Tree } from "antd";
import { DataNode } from "antd/lib/tree";
import { uniqueId } from "lodash";
import { Link } from "react-router-dom";
import { IOfferingGroup, IOfferingGroupMember } from "../../types/IOfferingGroup";
import { TableColumn } from "../../../../lib/interfaces";
import { getDictionaryItemById, toDisplayFormat } from "../../../../lib/utils";
import { usePolicyTreeTableData } from "./utils/usePolicyTreeTableData";
import {
  DataNodeOfferingGroup,
  DataNodeOfferingGroupTree,
  IOfferingGroupParamsTableRecord,
} from "./utils/types";
import { Offer } from "../../../../lib/types";
import dots from "../../../../img/3dots.svg";
import { useAppSelector } from "../../../..";

const columnsWidths: Partial<Record<keyof IOfferingGroup, string>> = {
  name: "70%",
  fromDate: "270px",
};

interface ComponentProps {
  offeringGroup?: IOfferingGroup;
  handleShowHistoryModal: (record) => void;
}

export const PolicyTreeTable = ({ offeringGroup, handleShowHistoryModal }: ComponentProps) => {
  const [treeExpandedKeys, setTreeExpandedKeys] = useState<string[]>([]);

  const { offeringGroupsTypesDic } = useAppSelector((state) => state.offeringGroups);

  const getTreeDataItem = (
    offeringGroup: Partial<IOfferingGroup>,
    parentKey?: string,
    offeringGroupMember?: Partial<IOfferingGroupMember>,
    offering?: Offer
  ): DataNodeOfferingGroup => {
    const id = offeringGroup?.id || Number(uniqueId());

    const key = [parentKey, id].filter(Boolean).join("-");

    if (offering) {
      return {
        offeringGroup,
        offeringGroupMember,
        offering,
        id,
        parentId: parentKey,
        title: offering?.name,
        key,
        className: "policy-tree__row",
      };
    }
    return {
      ...offeringGroup,
      ...offeringGroupMember,
      offeringGroup,
      id,
      parentId: parentKey,
      title: offeringGroup?.name,
      key,
      children: offeringGroup?.offeringGroupMembers
        ? offeringGroup?.offeringGroupMembers?.flatMap((child, i) => {
            return getTreeDataItem(
              child.offeringGroup as Partial<IOfferingGroup>,
              key,
              child,
              child.offering
            );
          })
        : undefined,
      className: "policy-tree__row",
    };
  };

  const treeData = useMemo((): DataNodeOfferingGroup[] => {
    if (offeringGroup) {
      return [
        getTreeDataItem({
          ...offeringGroup,
        }),
      ];
    }
    return [];
  }, [offeringGroup]);

  const { tableData } = usePolicyTreeTableData({
    treeData: treeData[0] || [],
  });

  const displayedTableData = useMemo(
    () =>
      tableData.filter((el) =>
        el?.parentId ? treeExpandedKeys.includes(String(el?.parentId)) : true
      ),
    [tableData, treeExpandedKeys]
  );

  const titleRender = (record: DataNodeOfferingGroupTree) => {
    const {
      title,
      key,
      offering,
      offeringGroup,
      offeringGroupMember,
      inversion,
      groupingTypeId,
    } = record;

    const isRoot = String(key).split("-").length === 1;
    //  Строка главная
    if (isRoot) {
      const groupingTypeName = getDictionaryItemById(offeringGroupsTypesDic, groupingTypeId)?.name;

      return (
        <Row style={{ width: "100%" }} wrap={false}>
          <div
            className={`row-group-offering-root ${
              groupingTypeName && groupingTypeName?.length > 3 ? "align-center-root" : ""
            }`}
          >
            {groupingTypeId ? groupingTypeName?.replace(/^(.{3})(.*)$/, "$1 $2") : null}
          </div>
          <div>
            <h4>
              <Link to={`/offering-groups/${offeringGroup?.id}`}>{title}</Link>
            </h4>
          </div>
        </Row>
      );
    }
    if (offering) {
      // Строка предложения
      return (
        <Row style={{ width: "100%" }} wrap={false}>
          <div className="row-group-offering">{offeringGroupMember?.inversion ? "NOT" : null}</div>
          <div>
            {`${title || ""}`}
            <span className="cardinality-value">/</span>
            {`${offeringGroupMember?.offering?.extName || ""}`}
          </div>
        </Row>
      );
    }
    if (offeringGroup) {
      // Строка группы предложения

      const groupingTypeName = getDictionaryItemById(offeringGroupsTypesDic, groupingTypeId)?.name;

      return (
        <Row style={{ width: "100%" }} wrap={false}>
          <div
            className={`row-group-offering-not-absolute ${
              groupingTypeName && groupingTypeName?.length > 3 ? "align-center" : ""
            }`}
          >
            {groupingTypeId ? groupingTypeName?.replace(/^(.{3})(.*)$/, "$1 $2") : null}
          </div>
          <div className="row-group-offering">{inversion ? "NOT" : null}</div>
          <div>
            <Link to={`/offering-groups/${offeringGroup.id}`}>{title}</Link>
          </div>
        </Row>
      );
    }

    return <div>{title}</div>;
  };

  // Дефолтное открытие всех элементов дерева
  useEffect(() => {
    const allKeys: string[] = [];
    const addKey = ({ key, children }: DataNode) => {
      if (children && children.length > 0) {
        allKeys.push(String(key));
        children.forEach(addKey);
      }
    };
    treeData.forEach(addKey);
    setTreeExpandedKeys(allKeys);
  }, [treeData]);

  // По дефолту в keys остаются активными вложенные элементы свернутого узла, удаление таковых
  const onExpand = (keys: Key[], { expanded, node }) => {
    if (expanded) {
      setTreeExpandedKeys(keys.map(String));
    } else {
      setTreeExpandedKeys((prevKeys) => {
        const activeKeys = prevKeys.filter((key) => !String(key).startsWith(String(node.key)));
        return activeKeys;
      });
    }
  };

  // Отображение линий визуальных у дерева
  const showLine = useMemo(() => {
    if (offeringGroup?.offeringGroupMembers && offeringGroup?.offeringGroupMembers.length > 0) {
      return { showLeafIcon: false };
    }
    return false;
  }, [offeringGroup]);

  const getOfferingGroupColumns = (): TableColumn<IOfferingGroupParamsTableRecord>[] => {
    return [
      {
        title: (
          <Row justify="center">
            <Tooltip title="умолч | мин | макс">Количество</Tooltip>
          </Row>
        ),
        key: "id",
        dataIndex: "id",
        width: columnsWidths.name,
        hidden: false,
        render: (_, record) => {
          if (record.offering) {
            return (
              <Row justify="center" style={{ fontSize: 13, fontWeight: 700 }}>
                {`${record?.offeringGroupMember?.defaultCardinality || "-"}`}
                <span className="cardinality-value">|</span>
                {`${record?.offeringGroupMember?.minCardinality || "-"}`}
                <span className="cardinality-value"> | </span>
                {`${record?.offeringGroupMember?.maxCardinality || "-"}`}
              </Row>
            );
          }
          return null;
        },
      },

      {
        title: "Период",
        dataIndex: "id",
        key: "id",
        width: columnsWidths.fromDate,
        hidden: false,
        render: (_, record) => {
          const { key, offeringGroupMember, offeringGroup } = record;
          const isRoot = String(key).split("-").length === 1;

          if (isRoot) return null;

          if (offeringGroupMember?.fromDate && offeringGroupMember?.toDate) {
            return `${toDisplayFormat(offeringGroupMember.fromDate)} - ${toDisplayFormat(
              offeringGroupMember.toDate
            )}`;
          }
          if (offeringGroup?.fromDate && offeringGroup?.toDate) {
            return `${toDisplayFormat(offeringGroup.fromDate)} - ${toDisplayFormat(
              offeringGroup.toDate
            )}`;
          }

          return null;
        },
      },

      {
        dataIndex: "btns",
        key: "btns",
        width: "42px",
        style: { padding: "10px" },
        render: (_, record) => {
          const isRoot = String(record.key).split("-").length === 1;
          if (isRoot) return null;
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleShowHistoryModal(record);
              }}
              className="button-icon"
              style={{
                boxShadow: "none",
                border: 0,
                marginLeft: "-10px",
                outline: "none",
                cursor: "pointer",
              }}
              type="button"
            >
              <img src={dots} alt="dots" />
            </button>
          );
        },
      },
    ];
  };

  const [columns, setColumns] = useState<TableColumn<IOfferingGroupParamsTableRecord>[]>(
    getOfferingGroupColumns()
  );

  const rowClassName = (record: IOfferingGroupParamsTableRecord) => {
    const isRootOffer = !record?.parentId;
    const baseClassName = "product-params__tableRow";
    if (isRootOffer) return baseClassName;
    // if (record.isCurrentVersion) {
    //   return `${baseClassName} row_child row_current-version`;
    // }
    // if (record.isPastVersion) {
    //   return `${baseClassName} row_child row_past-version`;
    // }
    return `${baseClassName}`;
  };

  return (
    <Row className="policy-tree">
      <Col span={7}>
        <div className="policy-tree__title">Наименование</div>
        <Tree
          showLine={showLine}
          treeData={treeData}
          showIcon={false}
          onExpand={onExpand}
          expandedKeys={treeExpandedKeys}
          titleRender={titleRender}
          selectable={false}
        />
      </Col>
      <Col span={17}>
        <Table
          size="small"
          columns={columns.filter(({ hidden }) => !hidden)}
          dataSource={displayedTableData}
          pagination={false}
          rowClassName={rowClassName}
          childrenColumnName="NONE"
        />
      </Col>
    </Row>
  );
};

export default PolicyTreeTable;
